import React, { useState, useMemo } from "react";
import {
  Plus, Search, MessageSquare, ImageIcon, Sparkles, MoreHorizontal,
  Pin, PinOff, Pencil, Trash2, LogOut, PanelLeftClose,
  Folder, FolderPlus, ChevronRight, ChevronDown, FolderInput, FolderMinus, CreditCard,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useUpgrade } from "../context/UpgradeContext";

function groupByDate(convs) {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(new Date());
  const yesterday = today - 86400000;
  const week = today - 7 * 86400000;
  const groups = { Pinned: [], Today: [], Yesterday: [], "Previous 7 days": [], Older: [] };
  convs.forEach((c) => {
    if (c.pinned) { groups.Pinned.push(c); return; }
    const t = startOfDay(new Date(c.updated_at));
    if (t >= today) groups.Today.push(c);
    else if (t >= yesterday) groups.Yesterday.push(c);
    else if (t >= week) groups["Previous 7 days"].push(c);
    else groups.Older.push(c);
  });
  return groups;
}

export default function Sidebar({
  conversations, folders, activeId, onSelect, onNewChat, onNewImage,
  onRename, onDelete, onPin, onMove, onCreateFolder, onRenameFolder, onDeleteFolder, onClose,
}) {
  const { user, logout } = useAuth();
  const { isPro, openUpgrade, openBillingPortal, subscription } = useUpgrade();
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [expanded, setExpanded] = useState({});
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderVal, setFolderVal] = useState("");

  const filtered = useMemo(
    () => conversations.filter((c) => (c.title || "").toLowerCase().includes(query.toLowerCase())),
    [conversations, query]
  );
  const ungrouped = useMemo(() => filtered.filter((c) => !c.folder_id), [filtered]);
  const groups = useMemo(() => groupByDate(ungrouped), [ungrouped]);

  const submitRename = (id) => {
    if (editVal.trim()) onRename(id, editVal.trim());
    setEditingId(null);
  };
  const submitFolder = () => {
    if (newFolderName.trim()) onCreateFolder(newFolderName.trim());
    setNewFolderName("");
    setCreatingFolder(false);
  };
  const submitFolderRename = (id) => {
    if (folderVal.trim()) onRenameFolder(id, folderVal.trim());
    setEditingFolder(null);
  };

  const ConvItem = ({ c }) => {
    const active = c.conversation_id === activeId;
    return (
      <div
        className={`group flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer ${active ? "bg-white shadow-sm border border-[#E6E6DF]" : "hover:bg-[#EFEFEA] border border-transparent"}`}
        style={{ transition: "background-color 0.15s ease" }}
        onClick={() => onSelect(c.conversation_id)}
        data-testid={`conversation-item-${c.conversation_id}`}
      >
        {c.mode === "image"
          ? <ImageIcon className="h-4 w-4 shrink-0 text-[#1A73E8]" />
          : <MessageSquare className="h-4 w-4 shrink-0 text-[#8C8C85]" />}
        {editingId === c.conversation_id ? (
          <input
            autoFocus
            value={editVal}
            onChange={(e) => setEditVal(e.target.value)}
            onBlur={() => submitRename(c.conversation_id)}
            onKeyDown={(e) => e.key === "Enter" && submitRename(c.conversation_id)}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-sm bg-transparent border-b border-[#2D6A4F] focus:outline-none"
            data-testid="rename-input"
          />
        ) : (
          <span className="flex-1 truncate text-sm text-[#26261F]">{c.title}</span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="opacity-0 group-hover:opacity-100 text-[#8C8C85] hover:text-[#171716] p-0.5" data-testid={`conversation-menu-${c.conversation_id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48" onCloseAutoFocus={(e) => e.preventDefault()}>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPin(c.conversation_id); }} data-testid="pin-conversation">
              {c.pinned ? <PinOff className="h-4 w-4 mr-2" /> : <Pin className="h-4 w-4 mr-2" />}
              {c.pinned ? "Unpin" : "Pin"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingId(c.conversation_id); setEditVal(c.title); }} data-testid="rename-conversation">
              <Pencil className="h-4 w-4 mr-2" /> Rename
            </DropdownMenuItem>
            {(folders && folders.length > 0) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#8C8C85]">Move to</DropdownMenuLabel>
                {folders.map((f) => (
                  <DropdownMenuItem
                    key={f.folder_id}
                    onClick={(e) => { e.stopPropagation(); onMove(c.conversation_id, f.folder_id); }}
                    data-testid={`move-to-${f.folder_id}`}
                  >
                    <FolderInput className="h-4 w-4 mr-2" /> {f.name}
                  </DropdownMenuItem>
                ))}
                {c.folder_id && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(c.conversation_id, null); }} data-testid="remove-from-folder">
                    <FolderMinus className="h-4 w-4 mr-2" /> Remove from folder
                  </DropdownMenuItem>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(c.conversation_id); }} className="text-red-600 focus:text-red-600" data-testid="delete-conversation">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <aside className="h-full w-full flex flex-col bg-[#F3F3EF] border-r border-[#E6E6DF]" data-testid="sidebar-container">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-[#1F2421] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight text-[#171716]">OmniAI</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-[#8C8C85] hover:text-[#171716] p-1" data-testid="sidebar-close">
            <PanelLeftClose className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="px-3 space-y-2">
        <button onClick={onNewChat} data-testid="new-chat-button" className="w-full flex items-center gap-2.5 rounded-xl bg-[#1F2421] text-white px-3.5 py-2.5 text-sm font-medium hover:bg-[#2D6A4F]" style={{ transition: "background-color 0.2s ease" }}>
          <Plus className="h-4 w-4" /> New chat
        </button>
        <button onClick={onNewImage} data-testid="new-image-button" className="w-full flex items-center gap-2.5 rounded-xl bg-white border border-[#E6E6DF] text-[#171716] px-3.5 py-2.5 text-sm font-medium hover:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }}>
          <ImageIcon className="h-4 w-4 text-[#1A73E8]" /> Image studio
        </button>
      </div>

      {/* Search */}
      <div className="px-3 mt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C85]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search chats" data-testid="search-chats-input" className="w-full rounded-xl bg-white border border-[#E6E6DF] pl-9 pr-3 py-2 text-sm placeholder:text-[#8C8C85] focus:outline-none focus:border-[#CECEBE]" />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto omni-scroll px-2 mt-3 pb-2" data-testid="conversation-list">
        {/* Folders */}
        <div className="mb-2">
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8C8C85]">Folders</span>
            <button onClick={() => setCreatingFolder(true)} className="text-[#8C8C85] hover:text-[#171716]" data-testid="new-folder-button" title="New folder">
              <FolderPlus className="h-4 w-4" />
            </button>
          </div>
          {creatingFolder && (
            <div className="px-2 pb-1">
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onBlur={submitFolder}
                onKeyDown={(e) => { if (e.key === "Enter") submitFolder(); if (e.key === "Escape") setCreatingFolder(false); }}
                placeholder="Folder name"
                data-testid="new-folder-input"
                className="w-full rounded-lg bg-white border border-[#CECEBE] px-2.5 py-1.5 text-sm focus:outline-none"
              />
            </div>
          )}
          {(folders || []).map((f) => {
            const items = filtered.filter((c) => c.folder_id === f.folder_id);
            const open = expanded[f.folder_id];
            return (
              <div key={f.folder_id}>
                <div className="group flex items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-[#EFEFEA] cursor-pointer" onClick={() => setExpanded((p) => ({ ...p, [f.folder_id]: !p[f.folder_id] }))} data-testid={`folder-${f.folder_id}`} style={{ transition: "background-color 0.15s ease" }}>
                  {open ? <ChevronDown className="h-4 w-4 text-[#8C8C85]" /> : <ChevronRight className="h-4 w-4 text-[#8C8C85]" />}
                  <Folder className="h-4 w-4 text-[#2D6A4F]" />
                  {editingFolder === f.folder_id ? (
                    <input
                      autoFocus
                      value={folderVal}
                      onChange={(e) => setFolderVal(e.target.value)}
                      onBlur={() => submitFolderRename(f.folder_id)}
                      onKeyDown={(e) => e.key === "Enter" && submitFolderRename(f.folder_id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="rename-folder-input"
                      className="flex-1 text-sm bg-transparent border-b border-[#2D6A4F] focus:outline-none"
                    />
                  ) : (
                    <span className="flex-1 truncate text-sm font-medium text-[#26261F]">{f.name}</span>
                  )}
                  <span className="text-[11px] text-[#8C8C85]">{items.length}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button className="opacity-0 group-hover:opacity-100 text-[#8C8C85] hover:text-[#171716] p-0.5" data-testid={`folder-menu-${f.folder_id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40" onCloseAutoFocus={(e) => e.preventDefault()}>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingFolder(f.folder_id); setFolderVal(f.name); }} data-testid="rename-folder">
                        <Pencil className="h-4 w-4 mr-2" /> Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDeleteFolder(f.folder_id); }} className="text-red-600 focus:text-red-600" data-testid="delete-folder">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {open && (
                  <div className="pl-5">
                    {items.length === 0
                      ? <p className="px-3 py-1.5 text-xs text-[#8C8C85]">Empty — move chats here.</p>
                      : items.map((c) => <ConvItem key={c.conversation_id} c={c} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Ungrouped chats grouped by date */}
        {Object.entries(groups).map(([label, list]) =>
          list.length === 0 ? null : (
            <div key={label} className="mb-3">
              <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#8C8C85]">{label}</p>
              {list.map((c) => <ConvItem key={c.conversation_id} c={c} />)}
            </div>
          )
        )}
        {filtered.length === 0 && <p className="text-sm text-[#8C8C85] px-3 py-6 text-center">No conversations yet.</p>}
      </div>

      {/* Upgrade CTA / plan */}
      {!isPro && (
        <div className="px-3 pb-1">
          <button
            onClick={() => openUpgrade("Unlock all 16 models, image generation and Compare Mode.")}
            data-testid="sidebar-upgrade-button"
            className="w-full rounded-xl bg-[#1F2421] text-white p-3 text-left hover:bg-[#2D6A4F] group"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#7FD3AC]" />
              <span className="text-sm font-semibold">Upgrade to Pro</span>
            </div>
            <p className="mt-1 text-xs text-white/70">
              {user?.usage ? `${user.usage.used_today}/${user.usage.limit} free messages used today` : "All models + images + compare"}
            </p>
          </button>
        </div>
      )}

      {/* Manage subscription (Pro with billing) */}
      {isPro && user?.has_billing && (
        <div className="px-3 pb-1">
          <button
            onClick={openBillingPortal}
            data-testid="manage-subscription-button"
            className="w-full flex items-center gap-2.5 rounded-xl bg-white border border-[#E6E6DF] px-3.5 py-2.5 text-sm font-medium text-[#171716] hover:border-[#CECEBE]"
            style={{ transition: "border-color 0.2s ease" }}
          >
            <CreditCard className="h-4 w-4 text-[#2D6A4F]" /> Manage subscription
          </button>
          {subscription?.cancel_at_period_end && (
            <p className="mt-2 px-1 text-xs text-[#8C6A2B] bg-[#FBF3E2] border border-[#F0E0B8] rounded-lg py-2 px-2.5" data-testid="cancellation-note">
              Your plan is set to cancel. You'll keep Pro until{" "}
              {subscription.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                : "the end of your billing period"}
              .
            </p>
          )}
        </div>
      )}

      {/* User card */}
      <div className="p-3 border-t border-[#E6E6DF]">
        <div className="flex items-center gap-3 rounded-xl bg-white border border-[#E6E6DF] p-2.5" data-testid="user-card">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center text-sm font-semibold">{(user?.name || "U").charAt(0)}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#171716] truncate">{user?.name || "User"}</p>
            <p className="text-xs truncate" data-testid="plan-label">
              {isPro
                ? <span className="text-[#2D6A4F] font-semibold">OmniAI Pro</span>
                : <span className="text-[#8C8C85]">Free plan</span>}
            </p>
          </div>
          <button onClick={logout} className="text-[#8C8C85] hover:text-red-600 p-1.5" data-testid="logout-button" title="Log out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
